import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    CreditCard, CheckCircle, Clock, Download, Receipt, Calendar,
    IndianRupee, AlertCircle, ArrowUpRight
} from 'lucide-react'


interface Payment {
    id: string
    description: string
    amount: number
    date: string
    status: 'paid' | 'pending' | 'failed'
    invoiceUrl?: string
}

const paymentHistory: Payment[] = [
    { id: 'PAY-001', description: 'October 2024 - Monthly Fee', amount: 15000, date: 'Oct 5, 2024', status: 'paid', invoiceUrl: '#' },
    { id: 'PAY-002', description: 'September 2024 - Monthly Fee', amount: 15000, date: 'Sep 5, 2024', status: 'paid', invoiceUrl: '#' },
    { id: 'PAY-003', description: 'August 2024 - Monthly Fee', amount: 15000, date: 'Aug 5, 2024', status: 'paid', invoiceUrl: '#' },
    { id: 'PAY-004', description: 'Enrollment Fee', amount: 5000, date: 'Aug 1, 2024', status: 'paid', invoiceUrl: '#' },
]

const upcomingPayments = [
    { description: 'November 2024 - Monthly Fee', amount: 15000, dueDate: 'Nov 5, 2024', daysRemaining: 16 },
]

const planDetails = {
    name: 'NEET 2025 Premium',
    price: 15000,
    cycle: 'Monthly',
    validUntil: 'May 2025',
    features: [
        'Unlimited Mock Tests',
        'Video Lectures',
        'Study Materials',
        'Doubt Clearing Sessions',
        'Performance Analytics',
        'Mentor Support',
    ]
}

export function PaymentsView() {
    const [showPaymentModal, setShowPaymentModal] = useState(false)

    const totalPaid = paymentHistory.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0)

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary flex items-center gap-2">
                    <CreditCard className="h-6 w-6 text-primary-blue" />
                    Payments
                </h2>
            </div>

            {/* Current Plan */}
            <GlassmorphicCard className="p-6 relative overflow-hidden bg-gradient-to-br from-primary-blue to-blue-600">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                <div className="relative text-white">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <p className="text-blue-100 text-sm mb-1">Your Current Plan</p>
                            <h3 className="text-2xl font-bold">{planDetails.name}</h3>
                        </div>
                        <div className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                            Active
                        </div>
                    </div>
                    <div className="flex items-baseline gap-1 mb-4">
                        <span className="text-3xl font-bold">₹{planDetails.price.toLocaleString()}</span>
                        <span className="text-blue-100">/{planDetails.cycle}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-100">
                        <Calendar className="h-4 w-4" />
                        Valid until {planDetails.validUntil}
                    </div>
                </div>
            </GlassmorphicCard>

            {/* Upcoming Payment */}
            {upcomingPayments.length > 0 && (
                <GlassmorphicCard className="p-4 md:p-6 border-l-4 border-l-orange-500">
                    <div className="flex items-center gap-2 mb-4">
                        <AlertCircle className="h-5 w-5 text-orange-500" />
                        <h3 className="font-bold text-theme-primary">Upcoming Payment</h3>
                    </div>
                    {upcomingPayments.map((payment, i) => (
                        <div key={i} className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-theme-primary">{payment.description}</p>
                                <p className="text-sm text-theme-secondary">Due: {payment.dueDate} ({payment.daysRemaining} days remaining)</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xl font-bold text-theme-primary flex items-center gap-1">
                                    <IndianRupee className="h-4 w-4" />{payment.amount.toLocaleString()}
                                </p>
                                <button
                                    onClick={() => setShowPaymentModal(true)}
                                    className="mt-2 px-4 py-2 bg-primary-blue text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                    Pay Now
                                </button>
                            </div>
                        </div>
                    ))}
                </GlassmorphicCard>
            )}

            {/* Payment Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <div>
                            <p className="text-xl font-bold text-theme-primary">₹{totalPaid.toLocaleString()}</p>
                            <p className="text-xs text-theme-secondary">Total Paid</p>
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <Receipt className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                            <p className="text-xl font-bold text-theme-primary">{paymentHistory.length}</p>
                            <p className="text-xs text-theme-secondary">Transactions</p>
                        </div>
                    </div>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 col-span-2 lg:col-span-1">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <Clock className="h-5 w-5 text-purple-500" />
                        </div>
                        <div>
                            <p className="text-xl font-bold text-theme-primary">8 months</p>
                            <p className="text-xs text-theme-secondary">Remaining</p>
                        </div>
                    </div>
                </GlassmorphicCard>
            </div>

            {/* Payment History */}
            <GlassmorphicCard className="overflow-hidden">
                <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                    <h3 className="font-bold text-theme-primary">Payment History</h3>
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {paymentHistory.map((payment, i) => (
                        <motion.div
                            key={payment.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${payment.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30' :
                                    payment.status === 'pending' ? 'bg-orange-100 dark:bg-orange-900/30' :
                                        'bg-red-100 dark:bg-red-900/30'
                                    }`}>
                                    {payment.status === 'paid' ? <CheckCircle className="h-5 w-5 text-green-500" /> :
                                        payment.status === 'pending' ? <Clock className="h-5 w-5 text-orange-500" /> :
                                            <AlertCircle className="h-5 w-5 text-red-500" />}
                                </div>
                                <div>
                                    <p className="font-medium text-theme-primary text-sm">{payment.description}</p>
                                    <div className="flex items-center gap-2 text-xs text-theme-secondary">
                                        <span>{payment.date}</span>
                                        <span>•</span>
                                        <span className="font-mono">{payment.id}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="text-right">
                                    <p className="font-bold text-theme-primary">₹{payment.amount.toLocaleString()}</p>
                                    <span className={`text-xs font-medium capitalize ${payment.status === 'paid' ? 'text-green-500' :
                                        payment.status === 'pending' ? 'text-orange-500' :
                                            'text-red-500'
                                        }`}>
                                        {payment.status}
                                    </span>
                                </div>
                                {payment.invoiceUrl && payment.status === 'paid' && (
                                    <button className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-primary-blue hover:text-white transition-colors text-slate-500">
                                        <Download className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </GlassmorphicCard>

            {/* Plan Features */}
            <GlassmorphicCard className="p-4 md:p-6">
                <h3 className="font-bold text-theme-primary mb-4">Plan Features</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                    {planDetails.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                            <span className="text-sm text-theme-secondary">{feature}</span>
                        </div>
                    ))}
                </div>
            </GlassmorphicCard>

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-md"
                    >
                        <h3 className="text-xl font-bold text-theme-primary mb-4">Complete Payment</h3>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl mb-4">
                            <div className="flex justify-between mb-2">
                                <span className="text-theme-secondary">Amount</span>
                                <span className="font-bold text-theme-primary">₹15,000</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-theme-secondary">For</span>
                                <span className="text-theme-primary">November 2024</span>
                            </div>
                        </div>
                        <p className="text-sm text-theme-secondary mb-4">
                            You will be redirected to our secure payment gateway to complete this transaction.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowPaymentModal(false)}
                                className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-theme-primary rounded-xl font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                className="flex-1 py-3 bg-primary-blue text-white rounded-xl font-medium hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                            >
                                Pay ₹15,000
                                <ArrowUpRight className="h-4 w-4" />
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    )
}
